package co.maytes.standupbot.dashboard.components.motion

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.shared.motion.PresencePhase
import co.maytes.standupbot.shared.motion.PresenceReducer
import co.maytes.standupbot.shared.motion.PresenceState
import co.maytes.standupbot.shared.motion.PresenceTiming
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlin.coroutines.resume

private class RetainedContent(var content: @Composable (PresenceState) -> Unit)

private const val RETRY_MS = 4L

private val settledState = PresenceState(PresencePhase.Present, shown = true, deadlineMs = null)

@Composable
fun Presence(
    visible: Boolean,
    enterMs: Int,
    exitMs: Int,
    animateInitial: Boolean = true,
    content: @Composable (PresenceState) -> Unit,
) {
    val timing = remember(enterMs, exitMs) { PresenceTiming(enterMs.toLong(), exitMs.toLong()) }
    var stored by remember { mutableStateOf(if (visible && !animateInitial) settledState else PresenceState()) }
    val retained = remember { RetainedContent(content) }
    if (visible) retained.content = content

    val current = PresenceReducer.visibilityChanged(stored, visible, nowMs(), timing)

    LaunchedEffect(visible) {
        stored = PresenceReducer.visibilityChanged(stored, visible, nowMs(), timing)
        if (stored.phase == PresencePhase.Entering && !stored.shown) {
            awaitNextFrame()
            stored = PresenceReducer.painted(stored, nowMs(), timing)
        }
    }

    LaunchedEffect(stored.deadlineMs) {
        while (true) {
            val remaining = PresenceReducer.remainingMs(stored, nowMs()) ?: return@LaunchedEffect
            if (remaining > 0) delay(remaining)
            val next = PresenceReducer.elapsed(stored, nowMs())
            if (next != stored) {
                stored = next
                return@LaunchedEffect
            }
            delay(RETRY_MS)
        }
    }

    if (current.mounted) retained.content(current)
}

fun presenceClassNames(baseClass: String, state: PresenceState): Array<String> =
    if (state.shown) arrayOf(baseClass, Motion.OPEN_CLASS) else arrayOf(baseClass)

fun Modifier.presenceClasses(baseClass: String, state: PresenceState): Modifier =
    classNames(*presenceClassNames(baseClass, state))

private fun nowMs(): Long = window.performance.now().toLong()

private suspend fun awaitNextFrame() = suspendCancellableCoroutine { continuation ->
    val handle = window.requestAnimationFrame { continuation.resume(Unit) }
    continuation.invokeOnCancellation { window.cancelAnimationFrame(handle) }
}
